import { Button, Group, List, Paper, Stack, Text } from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import type { ReactNode } from 'react';
import { useSettingsChanges } from '../../hooks/use-settings-changes';
import type {
  NotificationSettingsChanges,
  NotificationSettingsResponse,
  NotificationSettingsScope,
} from '../../types/notification-settings';
import { NotificationTypeCard } from './notification-type-card';
import { describeChanges } from './settings-changes';

interface NotificationSettingsEditorProps {
  response: NotificationSettingsResponse;
  scope: NotificationSettingsScope;
  saving: boolean;
  onSave: (changes: NotificationSettingsChanges) => Promise<unknown>;
  actions?: ReactNode;
}

export function NotificationSettingsEditor({
  response,
  scope,
  saving,
  onSave,
  actions,
}: NotificationSettingsEditorProps) {
  const { changes, changeCount, setChange, clearChanges } = useSettingsChanges();

  const save = async (): Promise<void> => {
    try {
      await onSave(changes);
      clearChanges();
      showNotification({
        title: 'Saved',
        message: 'Notification settings updated',
        color: 'green',
      });
    } catch (error) {
      showNotification({
        title: 'Save failed',
        message: (error as Error)?.message ?? 'Unknown error',
        color: 'red',
      });
    }
  };

  const confirmAndSave = (): void =>
    openConfirmModal({
      title: 'Change the global notification settings?',
      children: (
        <Stack spacing="xs">
          <Text size="sm">These apply to every account that does not set its own value.</Text>
          <List size="sm">
            {describeChanges(changes).map((line) => (
              <List.Item key={line}>{line}</List.Item>
            ))}
          </List>
        </Stack>
      ),
      labels: { confirm: 'Save', cancel: 'Cancel' },
      confirmProps: { color: 'bagel' },
      onConfirm: () => {
        void save();
      },
    });

  return (
    <Stack spacing="md">
      <Paper withBorder p="sm">
        <Group position="apart">
          <Text size="sm" color="dimmed">
            {changeCount > 0 ? `${changeCount} unsaved change(s)` : 'No unsaved changes'}
          </Text>
          <Group spacing="xs">
            {actions}
            <Button variant="subtle" size="xs" disabled={changeCount === 0} onClick={clearChanges}>
              Discard
            </Button>
            <Button
              size="xs"
              color="bagel"
              disabled={changeCount === 0}
              loading={saving}
              onClick={scope === 'global' ? confirmAndSave : () => void save()}
            >
              Save
            </Button>
          </Group>
        </Group>
      </Paper>
      {Object.entries(response.rolesByType).map(([type, roles]) =>
        response.view[type] ? (
          <NotificationTypeCard
            key={type}
            type={type}
            roles={roles}
            view={response.view[type]}
            scope={scope}
            changes={changes}
            onChange={(field, value) => setChange(type, field, value)}
          />
        ) : null,
      )}
    </Stack>
  );
}
